import { useState } from "react";
import { useCanAccess, useGetList, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { formatMxn } from "../quoter/PlanSchedule";
import { ExpenseReimburseDialog } from "./ExpenseReimburseDialog";
import { splitReimbursements, sumAmounts } from "./expenseReportRules";
import { formatExpenseDate } from "./expenseScreenRules";
import type { Expense } from "./expenseTypes";
import { useSetReimbursed } from "./useSetReimbursed";

// Pepe Diaz's expenses are few; one bounded page is enough.
const PAGE_SIZE = 1000;

const ReimbursementSection = ({
  testId,
  title,
  rows,
  emptyKey,
  isReimbursedSection,
}: {
  testId: string;
  title: string;
  rows: Expense[];
  emptyKey: string;
  isReimbursedSection: boolean;
}) => {
  const translate = useTranslate();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { mutate, isPending } = useSetReimbursed(() =>
    setSelectedIds(new Set()),
  );
  // Selection survives refetches, but only rows still in this section count.
  const selected = rows.filter((row) => selectedIds.has(String(row.id)));
  const toggle = (id: string, isChecked: boolean) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (isChecked) next.add(id);
      else next.delete(id);
      return next;
    });
  const areAllSelected = rows.length > 0 && selected.length === rows.length;

  return (
    <section data-testid={testId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {title} ({rows.length})
        </h2>
        {selected.length > 0 ? (
          <Button
            type="button"
            variant={isReimbursedSection ? "outline" : "default"}
            className="min-h-11"
            disabled={isPending}
            onClick={() =>
              isReimbursedSection
                ? mutate({ expenses: selected, reimbursedAt: null })
                : setIsDialogOpen(true)
            }
          >
            {translate(
              isReimbursedSection
                ? "crm.expenses.reimbursement.unmark_selected"
                : "crm.expenses.reimbursement.mark_selected",
              { smart_count: selected.length },
            )}
          </Button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <ListEmptyState title={translate(emptyKey)} className="min-h-[15vh]" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  aria-label={translate(
                    "crm.expenses.reimbursement.select_all",
                  )}
                  checked={areAllSelected}
                  onCheckedChange={(isChecked) =>
                    setSelectedIds(
                      isChecked
                        ? new Set(rows.map((row) => String(row.id)))
                        : new Set(),
                    )
                  }
                />
              </TableHead>
              <TableHead>{translate("crm.expenses.fields.date")}</TableHead>
              <TableHead>{translate("crm.expenses.fields.concept")}</TableHead>
              <TableHead className="text-right">
                {translate("crm.expenses.fields.amount")}
              </TableHead>
              {isReimbursedSection ? (
                <TableHead>
                  {translate("crm.expenses.reimbursement.reimbursed_on")}
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Checkbox
                    aria-label={translate(
                      "crm.expenses.reimbursement.select_row",
                      { concept: row.concept },
                    )}
                    checked={selectedIds.has(String(row.id))}
                    onCheckedChange={(isChecked) =>
                      toggle(String(row.id), isChecked === true)
                    }
                  />
                </TableCell>
                <TableCell className="tabular-nums">
                  {formatExpenseDate(row.expense_date)}
                </TableCell>
                <TableCell>
                  <Link
                    to={`/expenses/${row.id}/show`}
                    className="underline-offset-4 hover:underline"
                  >
                    {row.concept}
                  </Link>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMxn(row.amount_mxn)}
                </TableCell>
                {isReimbursedSection ? (
                  <TableCell className="tabular-nums">
                    {formatExpenseDate(row.reimbursed_at)}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <ExpenseReimburseDialog
        expenses={selected}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </section>
  );
};

/**
 * Almikia Epic EG (TASK-045): "Por reembolsar al socio". Lists the uncancelled
 * expenses paid by Pepe Diaz (the only partner; Sibaris and Otro are not
 * reimbursable) split into pending and reimbursed, with the pending total.
 * Gated to the four expense roles (redirects everyone else).
 */
export const ExpenseReimbursements = () => {
  const translate = useTranslate();
  const { canAccess, isPending: isAccessPending } = useCanAccess({
    resource: "expenses",
    action: "edit",
  });
  const { data, isPending, error, refetch } = useGetList<Expense>(
    "expenses",
    {
      filter: { paid_by: "pepe_diaz", is_cancelled: false },
      pagination: { page: 1, perPage: PAGE_SIZE },
      sort: { field: "expense_date", order: "DESC" },
    },
    { enabled: canAccess === true },
  );
  if (isAccessPending) return null;
  if (!canAccess) return <Navigate to="/" replace />;

  const { pending, reimbursed } = splitReimbursements(data ?? []);

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <header className="space-y-1">
        <Button asChild variant="ghost" className="-ml-3 min-h-11">
          <Link to="/expenses">{translate("crm.expenses.back_to_list")}</Link>
        </Button>
        <h1 className="text-2xl font-semibold">
          {translate("crm.expenses.reimbursement.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("crm.expenses.reimbursement.subtitle")}
        </p>
      </header>
      {error ? (
        <ListErrorState
          message={translate("crm.expenses.reimbursement.error")}
          onRetry={() => refetch()}
        />
      ) : isPending ? (
        <ListRowsSkeleton rows={4} />
      ) : (
        <>
          <div
            data-testid="reimbursement-pending-total"
            className="flex flex-wrap items-baseline gap-2 rounded-lg bg-card p-4 shadow-sm"
          >
            <span className="text-sm text-muted-foreground">
              {translate("crm.expenses.reimbursement.pending_total")}
            </span>
            <span className="text-2xl font-bold tabular-nums text-primary">
              {formatMxn(sumAmounts(pending))}
            </span>
          </div>
          <ReimbursementSection
            testId="reimbursement-pending"
            title={translate("crm.expenses.reimbursement.pending_section")}
            rows={pending}
            emptyKey="crm.expenses.reimbursement.empty_pending"
            isReimbursedSection={false}
          />
          <ReimbursementSection
            testId="reimbursement-done"
            title={translate("crm.expenses.reimbursement.done_section")}
            rows={reimbursed}
            emptyKey="crm.expenses.reimbursement.empty_done"
            isReimbursedSection
          />
        </>
      )}
    </div>
  );
};

ExpenseReimbursements.path = "/gastos-reembolsos";
